import type { RaceJoinOptions, SeatReservation } from '@runkr/schema'
import type { ComponentType } from 'preact'
import { refusalReason } from '../net/leaveReason'
import type { LeaveReason } from '../net/leaveReason'
import { Store } from '../net/store'
import { makePartyBar, makePartyPanel } from './lazyPartyUi'
import type { PartyBarProps } from './PartyBar'
import type { PartyConnector, PartyLink } from './partyNet'
import { QUEUE_FAILED, WAITING_FOR_LEADER, partyView } from './partyView'
import type { PartySnapshot, PartyView } from './partyView'
import { shareLink } from './shareLink'

export interface PartyDeps {
  connector: PartyConnector
  /** `location.origin`: the share link's base. */
  origin: string
  /** The party queued: take this seat in the race (the session's join). */
  onSeat: (seat: SeatReservation) => void
  /**
   * This player's race join options, from the one builder a solo join uses
   * (`GameClient.joinOptions`): sent to the party room, which reserves our seats with them.
   */
  options: () => Promise<RaceJoinOptions>
}

/** The options a party passes into our seats: everything but the token (it goes on connect). */
export function seatOptions(options: RaceJoinOptions): RaceJoinOptions {
  const copy = { ...options }
  delete copy.token
  return copy
}

/**
 * This tab's party (PHASE2 T2.7, D-040; D-053 slot `party` = `{ state, Panel }`). One party per
 * tab: create one, or join one by its code (share link, code box). The leader's `play()` queues
 * the whole party into one public race; a member's `play()` only says it waits for the leader.
 * Each `"seat"` goes to `onSeat`. `Panel` (the hub's Play with friends popover) and `Bar` (over
 * the hub and the results, mounted by `App`) are lazy chunks.
 */
export class Party {
  readonly state = new Store<PartyView | null>(null)
  /** Creating or joining. */
  readonly pending = new Store<boolean>(false)
  /** Why the last create/join failed, for the panel; null when it did not. */
  readonly error = new Store<LeaveReason | null>(null)
  readonly Panel: ComponentType
  readonly Bar: ComponentType<PartyBarProps>
  private link: PartyLink | null = null
  private snap: PartySnapshot | null = null
  private notice: string | null = null

  constructor(private readonly deps: PartyDeps) {
    this.Panel = makePartyPanel(this)
    this.Bar = makePartyBar(this)
  }

  /** In a party (joined, the state may still be on its way). */
  get inParty(): boolean {
    return this.link !== null
  }

  create(): Promise<LeaveReason | null> {
    return this.connect(() => this.deps.connector.create())
  }

  /** Join the party `roomId` with its code; the refusal's reason, or null when joined. */
  join(roomId: string, code: string): Promise<LeaveReason | null> {
    return this.connect(() => this.deps.connector.join(roomId, code))
  }

  /**
   * Race / Race again in a party (`session.playPublic()`): the leader queues everyone; a member
   * is told to wait for the leader. False outside a party (the caller plays alone).
   */
  play(): boolean {
    const link = this.link
    if (!link) return false
    const view = this.state.get()
    if (!view) return true
    if (!view.isLeader) {
      this.setNotice(WAITING_FOR_LEADER)
    } else if (view.status !== 'queueing') {
      this.setNotice(null)
      // Our current options first, so the leader's seat carries them too.
      void this.sendOptions(link).then(() => {
        if (this.link === link) link.send('queue')
      })
    }
    return true
  }

  /**
   * Send our current race join options to the party (the session calls this on the hub and the
   * results; a picker that changes an option may call it too), so our next seat carries them.
   */
  refreshOptions(): void {
    if (this.link) void this.sendOptions(this.link)
  }

  private async sendOptions(link: PartyLink): Promise<void> {
    const options = await this.deps.options().catch(() => null)
    if (options && this.link === link) link.sendOptions(seatOptions(options))
  }

  async leave(): Promise<void> {
    const link = this.link
    this.detach()
    await link?.leave()
  }

  private async connect(open: () => Promise<PartyLink>): Promise<LeaveReason | null> {
    if (this.link || this.pending.get()) return null
    this.pending.set(true)
    this.error.set(null)
    try {
      this.attach(await open())
      return null
    } catch (err) {
      console.warn('[party] join failed', err)
      const reason = refusalReason(err) ?? 'network'
      this.error.set(reason)
      return reason
    } finally {
      this.pending.set(false)
    }
  }

  private attach(link: PartyLink): void {
    this.link = link
    this.notice = null
    link.onState((snap) => {
      if (this.link !== link) return
      this.snap = snap
      this.render()
    })
    link.onSeat((seat) => {
      if (this.link !== link) return
      this.setNotice(null)
      this.deps.onSeat(seat)
    })
    link.onQueueFailed(() => {
      if (this.link === link) this.setNotice(QUEUE_FAILED)
    })
    link.onLeave(() => {
      if (this.link === link) this.detach()
    })
  }

  private detach(): void {
    this.link = null
    this.snap = null
    this.notice = null
    this.state.set(null)
  }

  private setNotice(notice: string | null): void {
    this.notice = notice
    this.render()
  }

  private render(): void {
    const { snap, link } = this
    if (!snap || !link) return
    const view = partyView(
      snap,
      link.sessionId,
      shareLink(this.deps.origin, snap.code),
      this.notice,
    )
    this.state.set(view)
  }
}
